/*
  fs-byod.js
  Behaviour for sections/fs-byod.liquid (Build your own Duo):
  - two picks select the matching bundle variant; one coffee can fill both bags
    if its stock allows
  - hero photo, badge, prices, offer strip, final call to action and every add
    button follow the pick
  - add buttons add the variant over AJAX and open the theme's cart drawer
    (falls back to a normal form post without it)
  - the coffee panels pin under the header and slide as you scroll; once
    scrolled past they become arrows (remembered), and they stack when a panel
    is too tall for the screen
  - sticky add bar once the buy-box button scrolls under the header
*/
if (!customElements.get('fs-byod')) {
  customElements.define(
    'fs-byod',
    class FsByod extends HTMLElement {
      connectedCallback() {
        const data = this.readData();
        this.coffees = data.coffees || [];
        this.strings = data.strings || {};
        this.moneyFormat = String(data.moneyFormat || '£{{amount}}').replace(/<[^>]*>/g, '');
        this.month = data.month || '';

        this.nameIndex = new Map();
        this.coffees.forEach((coffee, index) => {
          [coffee.name, coffee.short].forEach((name) => {
            if (name) this.nameIndex.set(this.norm(name), index);
          });
        });
        this.variants = new Map();
        (data.variants || []).forEach((variant) => {
          const key = this.pairKey(variant.title);
          if (key && !this.variants.has(key)) this.variants.set(key, variant);
        });
        this.pairs = new Map();
        (data.pairs || []).forEach((pair) => {
          const key = this.pairKey(pair.coffees);
          if (key && !this.pairs.has(key)) this.pairs.set(key, pair);
        });
        this.defaultPair = (data.pairs || [])[0] || null;

        this.picks = [];
        this.lastKey = null;
        this.added = false;

        this.form = this.querySelector('form[data-byod-form]');
        this.idInput = this.querySelector('[data-byod-variant-id]');
        this.errorEl = this.querySelector('[data-byod-error]');
        this.cards = Array.from(this.querySelectorAll('[data-byod-card]'));
        this.dots = Array.from(this.querySelectorAll('[data-byod-dot]'));
        this.countEl = this.querySelector('[data-byod-count]');
        this.addButtons = Array.from(this.querySelectorAll('[data-byod-add]'));
        this.heroSlot = this.querySelector('[data-byod-heroslot]');
        this.badgeEl = this.querySelector('[data-byod-badge]');
        const heroSource = this.querySelector('[data-byod-hero-pic] source');
        this.heroSizes = heroSource ? heroSource.getAttribute('sizes') : '100vw';
        this.heroImgKey = this.defaultPair ? this.defaultPair.coffees : null;

        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);

        this.render();
        this.placePromo();
        this.initSticky();
        this.initFlip();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        window.removeEventListener('scroll', this.requestStickyUpdate);
        window.removeEventListener('resize', this.requestStickyUpdate);
        window.removeEventListener('scroll', this.requestFlipUpdate);
        window.removeEventListener('resize', this.requestFlipMeasure);
        if (this.drawerObserver) this.drawerObserver.disconnect();
        if (this.addedTimer) clearTimeout(this.addedTimer);
        document.body.classList.remove('fs-duo-sticky-on');
      }

      readData() {
        const el = this.querySelector('[data-byod-data]');
        if (!el) return {};
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          console.error('fs-byod: could not read the page data', e);
          return {};
        }
      }

      /* ---------- Names, pairs and money ---------- */
      norm(value) {
        return String(value || '')
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();
      }

      // "Milan + Colombia", "Colombia + Milan" and "Milan x2" -> "0+2", "0+0" (coffee indexes, sorted)
      pairKey(label) {
        const text = this.norm(label);
        if (!text) return null;
        let names;
        const twice = text.match(/^(.+?)\s*[x×]\s*2$/);
        if (twice) {
          names = [twice[1], twice[1]];
        } else {
          names = text.split(/\s*\+\s*/);
          if (names.length !== 2) return null;
        }
        const indexes = names.map((name) => this.nameIndex.get(name.trim()));
        if (indexes.some((index) => index === undefined)) return null;
        return indexes.sort((a, b) => a - b).join('+');
      }

      money(cents) {
        const format = (decimals, thousands, decimal) => {
          const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? grouped + decimal + fraction : grouped;
        };
        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          if (key === 'amount_no_decimals') return format(0, ',', '.');
          if (key === 'amount_with_comma_separator') return format(2, '.', ',');
          if (key === 'amount_no_decimals_with_comma_separator') return format(0, '.', ',');
          return format(2, ',', '.');
        });
      }

      fill(template, pair) {
        const values = {
          count: String(this.picks.length),
          month: this.month,
        };
        if (pair) {
          values.name = pair.name;
          values.was = this.money(pair.was);
          values.saving = this.money(pair.save);
          if (pair.variant) values.price = this.money(pair.price);
        }
        return String(template || '').replace(/\(\((\w+)\)\)/g, (match, key) => (key in values ? values[key] : match));
      }

      /* ---------- State ---------- */
      countOf(index) {
        return this.picks.filter((pick) => pick === index).length;
      }

      setCount(index, quantity) {
        const coffee = this.coffees[index];
        if (!coffee) return;
        const others = this.picks.filter((pick) => pick !== index);
        const allowed = Math.max(0, Math.min(quantity, 2 - others.length, coffee.max));
        this.picks = others.concat(Array(allowed).fill(index));
        this.changed();
      }

      addOne(index) {
        const coffee = this.coffees[index];
        if (!coffee || this.picks.length >= 2 || this.countOf(index) >= coffee.max) return;
        this.picks = this.picks.concat(index);
        this.changed();
      }

      removeOne(index) {
        const at = this.picks.lastIndexOf(index);
        if (at < 0) return;
        this.picks = this.picks.filter((pick, i) => i !== at);
        this.changed();
      }

      changed() {
        this.added = false;
        if (this.addedTimer) clearTimeout(this.addedTimer);
        this.hideError();
        this.render();
      }

      pair() {
        if (this.picks.length < 2) return null;
        const [first, second] = this.picks;
        const key = [first, second].sort((a, b) => a - b).join('+');
        const a = this.coffees[first];
        const b = this.coffees[second];
        const variant = this.variants.get(key) || null;
        const sum = a.price + b.price;
        const price = variant ? variant.price : sum;
        const was = variant && variant.compare > variant.price ? variant.compare : sum;
        return {
          key,
          name: first === second ? a.name + ' x2' : a.name + ' + ' + b.name,
          variant,
          price,
          was,
          save: Math.max(0, was - price),
          available: !!(variant && variant.available),
        };
      }

      /* ---------- Rendering ---------- */
      render() {
        const s = this.strings;
        const pair = this.pair();
        const count = this.picks.length;
        const full = count >= 2;
        if (pair) this.lastKey = pair.key;

        this.renderHero(pair ? pair.key : this.lastKey);
        this.renderPrices(pair);

        this.cards.forEach((card) => {
          const index = Number(card.dataset.index);
          const coffee = this.coffees[index];
          if (!coffee) return;
          const n = this.countOf(index);
          const out = coffee.max <= 0;
          card.classList.toggle('is-on', n > 0);
          card.classList.toggle('is-two', n === 2);
          card.classList.toggle('is-dim', n === 0 && (full || out));
          const pick = card.querySelector('[data-byod-pick]');
          if (pick) pick.disabled = full || out || n >= coffee.max;
          const qty = card.querySelector('[data-byod-qty]');
          if (qty) qty.hidden = n === 0;
          const value = card.querySelector('[data-byod-qty-value]');
          if (value) value.textContent = String(n);
          const inc = card.querySelector('[data-byod-inc]');
          if (inc) inc.disabled = n >= Math.min(coffee.max, n + (2 - count));
        });

        this.dots.forEach((dot, i) => dot.classList.toggle('is-on', count > i));
        if (this.countEl) this.countEl.textContent = this.fill(s.count);

        this.addButtons.forEach((button) => this.renderAdd(button, pair));

        const stripTitle = this.querySelector('[data-byod-strip-title]');
        const stripText = this.querySelector('[data-byod-strip-text]');
        if (stripTitle) stripTitle.textContent = pair ? this.fill(s.strip1TitlePair, pair) : s.strip1Title;
        if (stripText) stripText.textContent = pair ? this.fill(s.strip1TextPair, pair) : s.strip1Text;

        this.querySelectorAll('[data-byod-panel]').forEach((panel) => this.renderPanel(panel, full));

        const finalHeading = this.querySelector('[data-byod-final-heading]');
        const finalText = this.querySelector('[data-byod-final-text]');
        if (finalHeading) finalHeading.textContent = pair ? pair.name : s.finalHeading;
        if (finalText) finalText.textContent = pair ? this.fill(s.finalTextPair, pair) : s.finalText;

        const stickyName = this.querySelector('[data-byod-sticky-name]');
        const stickyMeta = this.querySelector('[data-byod-sticky-meta]');
        if (stickyName) stickyName.textContent = pair ? pair.name : s.finalHeading;
        if (stickyMeta) stickyMeta.textContent = this.fill(pair ? s.stickyMetaPair : s.stickyMeta, pair);

        if (this.idInput) this.idInput.value = pair && pair.variant ? pair.variant.id : '';
      }

      renderHero(key) {
        const s = this.strings;
        const pair = key ? this.pairs.get(key) : null;
        if (this.badgeEl) {
          this.badgeEl.textContent = key
            ? (pair && pair.badge) || this.fill(s.badgeFallback)
            : this.fill(s.badgeDefault);
        }
        const source = pair && pair.m && pair.m.src ? pair : this.defaultPair;
        if (!source || !source.m || !source.m.src || source.coffees === this.heroImgKey) return;
        const current = this.heroSlot && this.heroSlot.querySelector('[data-byod-hero-pic]');
        if (!current) return;

        const picture = document.createElement('picture');
        picture.className = 'byod-hero-pic';
        picture.setAttribute('data-byod-hero-pic', '');
        if (source.d && source.d.srcset) {
          const wide = document.createElement('source');
          wide.media = '(min-width: 600px)';
          wide.srcset = source.d.srcset;
          wide.sizes = this.heroSizes;
          picture.appendChild(wide);
        }
        const img = document.createElement('img');
        img.className = 'byod-hero-photo';
        img.src = source.m.src;
        if (source.m.srcset) img.srcset = source.m.srcset;
        img.sizes = '100vw';
        if (source.m.w) img.width = source.m.w;
        if (source.m.h) img.height = source.m.h;
        img.alt = source.alt || '';
        picture.appendChild(img);
        current.replaceWith(picture);
        this.heroImgKey = source.coffees;
      }

      renderPrices(pair) {
        const now = this.querySelector('[data-byod-pnow]');
        const wasWrap = this.querySelector('[data-byod-pwas]');
        const was = this.querySelector('[data-byod-was]');
        const save = this.querySelector('[data-byod-psave]');
        const note = this.querySelector('[data-byod-pnote]');
        const priced = !!(pair && pair.variant);
        if (now) now.textContent = priced ? this.money(pair.price) : this.strings.offerPrice;
        if (wasWrap) wasWrap.hidden = !(priced && pair.was > pair.price);
        if (was && priced) was.textContent = 'was ' + this.money(pair.was);
        if (save) {
          save.hidden = !(priced && pair.save > 0);
          if (priced) save.textContent = 'Save ' + this.money(pair.save);
        }
        if (note) note.hidden = priced;
      }

      renderAdd(button, pair) {
        const s = this.strings;
        const text = button.querySelector('[data-byod-add-text]') || button;
        const where = button.dataset.byodAdd;
        let label;
        let disabled = false;
        if (!pair) {
          if (where === 'main') {
            label = this.picks.length === 1 ? s.pickOne : s.pickTwo;
            disabled = true;
          } else {
            label = where === 'final' ? s.finalChoose : s.stickyChoose;
          }
        } else if (!pair.available) {
          label = pair.variant ? s.soldOut : s.unavailable;
          disabled = true;
        } else if (where === 'final') {
          label = this.added ? s.finalAdded : s.finalButton;
        } else {
          label = this.added ? s.added : this.fill(s.add, pair);
        }
        button.disabled = disabled;
        text.textContent = label;
      }

      renderPanel(panel, full) {
        const s = this.strings;
        const index = Number(panel.dataset.byodPanel);
        const coffee = this.coffees[index];
        const main = panel.querySelector('[data-byod-flip-main]');
        const remove = panel.querySelector('[data-byod-flip-remove]');
        if (!coffee || !main) return;
        const n = this.countOf(index);
        const on = n > 0;
        let label;
        let action = '';
        let disabled = false;
        let primary = on;
        if (coffee.max <= 0 && !on) {
          label = s.flipSoldOut;
          disabled = true;
          primary = false;
        } else if (full) {
          label = on ? (n === 2 ? s.flipRemoveOne : s.flipRemove) : s.flipFull;
          disabled = !on;
          action = 'remove';
        } else {
          label = on ? s.flipSecond : s.flipAdd;
          disabled = on && coffee.max < 2;
          action = 'add';
        }
        main.textContent = label;
        main.disabled = disabled;
        main.dataset.action = action;
        main.classList.toggle('byod-btn--primary', primary);
        main.classList.toggle('byod-btn--outline', !primary);
        if (remove) remove.hidden = !(on && !full);
      }

      /* ---------- Clicks ---------- */
      onClick(event) {
        const button = event.target.closest('button');
        if (!button || !this.contains(button) || button.disabled) return;
        const card = button.closest('[data-byod-card]');
        const panel = button.closest('[data-byod-panel]');
        const cardIndex = card ? Number(card.dataset.index) : -1;
        const panelIndex = panel ? Number(panel.dataset.byodPanel) : -1;

        if (button.hasAttribute('data-byod-pick')) {
          this.setCount(cardIndex, this.countOf(cardIndex) + 1);
          this.keepFocus(card);
        } else if (button.hasAttribute('data-byod-inc')) {
          this.setCount(cardIndex, this.countOf(cardIndex) + 1);
          this.keepFocus(card);
        } else if (button.hasAttribute('data-byod-dec')) {
          this.setCount(cardIndex, this.countOf(cardIndex) - 1);
          this.keepFocus(card);
        } else if (button.hasAttribute('data-byod-remove')) {
          this.setCount(cardIndex, 0);
          this.keepFocus(card);
        } else if (button.hasAttribute('data-byod-flip-main')) {
          if (button.dataset.action === 'add') this.addOne(panelIndex);
          if (button.dataset.action === 'remove') this.removeOne(panelIndex);
        } else if (button.hasAttribute('data-byod-flip-remove')) {
          this.removeOne(panelIndex);
          if (button.hidden && panel) panel.querySelector('[data-byod-flip-main]').focus();
        } else if (button.hasAttribute('data-byod-add')) {
          if (this.pair()) this.addToCart(button);
          else this.scrollToPicker();
        } else if (button.hasAttribute('data-byod-tab')) {
          this.flipJump(Number(button.dataset.byodTab));
        } else if (button.hasAttribute('data-byod-prev')) {
          this.flipGo(this.flipIdx - 1);
        } else if (button.hasAttribute('data-byod-next')) {
          this.flipGo(this.flipIdx + 1);
        }
      }

      // Keyboard focus shouldn't fall back to the page when the focused control hides or disables.
      keepFocus(card) {
        const active = document.activeElement;
        if (!card || !active || !card.contains(active)) return;
        if (!active.disabled && !active.closest('[hidden]')) return;
        const next =
          card.querySelector('[data-byod-qty]:not([hidden]) [data-byod-dec]') ||
          card.querySelector('[data-byod-pick]:not(:disabled)');
        if (next) next.focus();
      }

      scrollToPicker() {
        const picker = this.querySelector('[data-byod-picker]');
        if (!picker) return;
        const top = picker.getBoundingClientRect().top + window.scrollY - this.pinTop() - 16;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        const first = picker.querySelector('[data-byod-pick]:not(:disabled)');
        if (first) first.focus({ preventScroll: true });
      }

      /* ---------- Add to cart ---------- */
      addToCart(button) {
        const pair = this.pair();
        if (!pair || !pair.available || this.busy || !this.form) return;
        if (this.idInput) this.idInput.value = pair.variant.id;

        const cart = document.querySelector('cart-drawer');
        if (!cart || typeof cart.renderContents !== 'function' || typeof cart.getSectionsToRender !== 'function') {
          this.form.submit();
          return;
        }

        this.busy = true;
        this.hideError();
        this.addButtons.forEach((b) => b.setAttribute('aria-busy', 'true'));

        const formData = new FormData(this.form);
        formData.append(
          'sections',
          cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);

        fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'fs-byod',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            this.showAdded();
            cart.classList.remove('is-empty');
            cart.renderContents(response);
          })
          .catch((error) => {
            console.error('fs-byod: add to cart failed', error);
            this.showError(this.strings.error || 'Something went wrong. Please try again.');
          })
          .finally(() => {
            this.busy = false;
            this.addButtons.forEach((b) => b.removeAttribute('aria-busy'));
          });
      }

      showAdded() {
        if (this.addedTimer) clearTimeout(this.addedTimer);
        this.added = true;
        this.render();
        this.addedTimer = setTimeout(() => {
          this.added = false;
          this.addedTimer = null;
          this.render();
        }, parseInt(this.dataset.addedMs, 10) || 1600);
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      /* ---------- Header offset ---------- */
      pinTop() {
        const header = document.querySelector('.section-header');
        if (!header || header.classList.contains('shopify-section-header-hidden')) return 0;
        const position = getComputedStyle(header).position;
        return position === 'sticky' || position === 'fixed' ? header.offsetHeight : 0;
      }

      /* ---------- Sticky add bar ---------- */
      initSticky() {
        this.sticky = this.querySelector('[data-byod-sticky]');
        this.anchor = this.querySelector('[data-byod-anchor]');
        if (!this.sticky || !this.anchor) return;
        this.requestStickyUpdate = () => {
          if (this.stickyFrame) return;
          this.stickyFrame = requestAnimationFrame(() => {
            this.stickyFrame = 0;
            this.updateSticky();
          });
        };
        window.addEventListener('scroll', this.requestStickyUpdate, { passive: true });
        window.addEventListener('resize', this.requestStickyUpdate);
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          this.drawerObserver = new MutationObserver(this.requestStickyUpdate);
          this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }
        this.updateSticky();
      }

      updateSticky() {
        const drawer = document.querySelector('cart-drawer');
        const drawerOpen = !!(drawer && drawer.classList.contains('active'));
        const show = this.anchor.getBoundingClientRect().bottom < this.pinTop() && !drawerOpen;
        if (show === this.stickyShown) return;

        this.stickyShown = show;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (show) {
          this.sticky.removeAttribute('inert');
          document.body.style.setProperty('--duo-sticky-height', `${this.sticky.offsetHeight}px`);
        } else {
          this.sticky.setAttribute('inert', '');
        }
        document.body.classList.toggle('fs-duo-sticky-on', show);
      }

      /* ---------- The coffees: scroll-through panels ---------- */
      initFlip() {
        this.flip = this.querySelector('[data-byod-flip]');
        if (!this.flip) return;
        this.track = this.flip.querySelector('[data-byod-track]');
        this.flipPanels = Array.from(this.flip.querySelectorAll('[data-byod-panel]'));
        this.tabs = Array.from(this.flip.querySelectorAll('[data-byod-tab]'));
        this.flipTop = this.flip.querySelector('.byod-fliptop');
        this.flipNav = this.flip.querySelector('[data-byod-flipnav]');
        this.flipPrev = this.flip.querySelector('[data-byod-prev]');
        this.flipNext = this.flip.querySelector('[data-byod-next]');
        this.flipHint = this.flip.querySelector('[data-byod-fliphint]');
        this.N = this.flipPanels.length;
        this.flipIdx = 0;
        this.flipX = 0;
        try {
          this.flipReleased = window.localStorage.getItem('byod-flip-released') === '1';
        } catch (e) {
          this.flipReleased = false;
        }

        this.requestFlipUpdate = () => {
          if (this.flipFrame) return;
          this.flipFrame = requestAnimationFrame(() => {
            this.flipFrame = 0;
            this.onFlipScroll();
          });
        };
        // Phone address bars resize the viewport while scrolling; only re-measure for real size changes.
        this.flipSize = [window.innerWidth, window.innerHeight];
        this.requestFlipMeasure = () => {
          const [w, h] = this.flipSize;
          if (window.innerWidth === w && Math.abs(window.innerHeight - h) < 120) return;
          this.flipSize = [window.innerWidth, window.innerHeight];
          if (this.flipMeasureFrame) return;
          this.flipMeasureFrame = requestAnimationFrame(() => {
            this.flipMeasureFrame = 0;
            this.measureFlip();
          });
        };
        window.addEventListener('scroll', this.requestFlipUpdate, { passive: true });
        window.addEventListener('resize', this.requestFlipMeasure);
        this.measureFlip();
      }

      // Pin only when every panel fits on screen under the header and tabs.
      measureFlip() {
        if (this.N < 2) {
          this.setFlipMode('stacked');
          return;
        }
        const top = this.pinTop();
        this.flip.style.setProperty('--byod-pin-top', `${top}px`);
        this.flip.classList.add('is-pinned');
        this.flip.classList.remove('is-browse');
        const pad = window.innerWidth >= 960 ? 40 : 84;
        const avail = window.innerHeight - top - (this.flipTop ? this.flipTop.offsetHeight : 0) - pad;
        let need = 0;
        this.flip.querySelectorAll('.byod-fp-grid').forEach((grid) => {
          need = Math.max(need, grid.scrollHeight);
        });
        const fits = need <= avail;
        this.setFlipMode(!fits ? 'stacked' : this.flipReleased ? 'browse' : 'pinned');
      }

      setFlipMode(mode) {
        this.flipMode = mode;
        this.flip.classList.toggle('is-pinned', mode === 'pinned');
        this.flip.classList.toggle('is-browse', mode === 'browse');
        this.flip.style.height = mode === 'pinned' ? `${this.N * 80 + 40}vh` : '';
        if (this.flipNav) this.flipNav.hidden = mode !== 'browse';
        if (mode === 'pinned') {
          const r = this.flip.getBoundingClientRect();
          if (r.bottom <= this.pinTop()) {
            this.releaseFlip();
            return;
          }
        }
        this.updateFlip();
      }

      flipProgress() {
        const top = this.pinTop();
        const r = this.flip.getBoundingClientRect();
        const total = Math.max(1, r.height - (window.innerHeight - top));
        return Math.min(1, Math.max(0, (top - r.top) / total));
      }

      onFlipScroll() {
        if (!this.flip) return;
        if (this.flipMode === 'pinned' && this.flip.getBoundingClientRect().bottom <= this.pinTop()) {
          this.releaseFlip();
          return;
        }
        this.updateFlip();
      }

      // Scrolled past the pinned panels: keep them one screen tall with arrows, without moving the page.
      releaseFlip() {
        try {
          window.localStorage.setItem('byod-flip-released', '1');
        } catch (e) {
          /* private mode */
        }
        this.flipReleased = true;
        const oldHeight = this.flip.offsetHeight;
        this.flipIdx = Math.round(this.flipX);
        this.setFlipMode('browse');
        window.scrollBy(0, this.flip.offsetHeight - oldHeight);
      }

      updateFlip() {
        const N = this.N;
        const mode = this.flipMode;
        let x = 0;
        let active = 0;
        if (mode === 'pinned') {
          const raw = this.flipProgress() * (N - 1);
          const seg = Math.min(N - 2, Math.floor(raw));
          const f = raw - seg;
          const t = f < 0.3 ? 0 : f > 0.7 ? 1 : (f - 0.3) / 0.4;
          x = seg + (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
          active = Math.round(x);
          this.flipX = x;
        } else if (mode === 'browse') {
          x = this.flipIdx;
          active = this.flipIdx;
        } else {
          const line = this.pinTop() + window.innerHeight * 0.3;
          this.flipPanels.forEach((panel, i) => {
            if (panel.getBoundingClientRect().top <= line) active = i;
          });
        }

        const sliding = mode === 'pinned' || mode === 'browse';
        if (this.track) this.track.style.transform = sliding ? `translateX(${(-x * 100) / N}%)` : '';
        this.tabs.forEach((tab, i) => {
          tab.classList.toggle('is-on', i === active);
          if (i === active) tab.setAttribute('aria-current', 'true');
          else tab.removeAttribute('aria-current');
        });
        this.flipPanels.forEach((panel, i) => {
          const hide = sliding && i !== active;
          panel.inert = hide;
          if (hide) panel.setAttribute('aria-hidden', 'true');
          else panel.removeAttribute('aria-hidden');
        });
        if (this.flipHint) this.flipHint.textContent = mode === 'pinned' && active < N - 1 ? this.strings.flipHint : '';
        if (this.flipPrev) this.flipPrev.disabled = this.flipIdx <= 0;
        if (this.flipNext) this.flipNext.disabled = this.flipIdx >= N - 1;
      }

      flipGo(index) {
        this.flipIdx = Math.max(0, Math.min(this.N - 1, index));
        this.updateFlip();
      }

      flipJump(index) {
        if (this.flipMode === 'browse') {
          this.flipGo(index);
          return;
        }
        const top = this.pinTop();
        if (this.flipMode !== 'pinned') {
          const panel = this.flipPanels[index];
          if (panel) {
            window.scrollTo({
              top: window.scrollY + panel.getBoundingClientRect().top - top - 16,
              behavior: 'smooth',
            });
          }
          return;
        }
        const r = this.flip.getBoundingClientRect();
        const total = r.height - (window.innerHeight - top);
        const p = Math.min(0.99, Math.max(0.01, index / (this.N - 1)));
        window.scrollTo({ top: window.scrollY + r.top - top + p * total, behavior: 'smooth' });
      }

      /* ---------- Offer line in the announcement bar slot ---------- */
      placePromo() {
        const promo = this.querySelector('[data-duo-promo][data-replace]');
        const bar = document.querySelector('.announcement-bar-section');
        if (!promo || !bar || !bar.parentNode) return;
        document.querySelectorAll('[data-duo-promo][data-placed]').forEach((node) => node.remove());
        promo.setAttribute('data-placed', '');
        bar.parentNode.insertBefore(promo, bar);
      }
    }
  );
}
